import type { ReactNode } from 'react'
import { AlertCircle, Loader2, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Centered spinner for a page or section that is loading. */
export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" className="flex justify-center py-16 text-muted-foreground">
      <Loader2 className="size-5 animate-spin" />
      <span className="sr-only">{label}</span>
    </div>
  )
}

/** An inline error callout: what failed, and how to recover when possible. */
export function ErrorState({ message, action, className }: { message: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-lg border border-danger-border bg-danger-subtle px-3 py-2 text-body text-danger',
        className,
      )}
    >
      <AlertCircle className="size-4 shrink-0" />
      <span className="min-w-0 flex-1">{message}</span>
      {action}
    </div>
  )
}

/** Nothing here yet: a short explanation and, optionally, the next step. */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: LucideIcon
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border-strong bg-card px-6 py-10 text-center">
      <span className="inline-flex size-9 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon className="size-4" />
      </span>
      <p className="text-body-lg font-medium">{title}</p>
      {description ? <p className="max-w-sm text-body text-muted-foreground">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  )
}
